import useOfferDetailViewerKey from 'tripPlanner/hooks/useOfferDetailViewerKey'
import { useContext, useEffect, useMemo, useRef } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import GeoContext from 'contexts/geoContext'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { getOfferDetailBatch } from 'tripPlanner/api/offerDetailBatch'
import type { BatchOfferDetailResult } from 'tripPlanner/api/types'
import * as OfferDetailKeys from 'tripPlanner/hooks/reactQueryKeys/offerDetail'
import { OFFER_DETAIL_STALE_TIME } from 'tripPlanner/hooks/api/queryConstants'
import { sanitiseChildrenAges } from 'lib/search/occupancy'
import type { CarouselOccupancy } from './ChatHotelCarousel'

interface OfferDetailBatchFilters {
  checkIn?: string
  checkOut?: string
  flexibleMonths?: string
  flexibleNights?: string
  occupancy?: CarouselOccupancy
}

/** Region + filters every id in one request is priced under. */
interface BatchRequestParams {
  viewerKey: string
  region: string
  checkIn?: string
  checkOut?: string
  flexibleMonths?: string
  flexibleNights?: string
  occupancies?: string
}

interface PendingEntry {
  resolve: (entry: BatchOfferDetailResult | null) => void
  reject: (error: unknown) => void
}

/**
 * Ids whose query function has started but whose request has not gone out yet, held per client
 * and then per the params they must be priced under, so two different sets of dates never share
 * a request and no state is shared between clients.
 *
 * Note this coalesces within a single flush, which in practice means one hook's commit — two
 * carousels showing the same offers do NOT share a request, because each flushes right after its
 * own subscribe effect, before the other has registered. What it does coalesce is every id one
 * commit asked for, plus any fetch that no commit drove (see the backstop in `registerOfferId`).
 */
const pendingBatchesByClient = new WeakMap<QueryClient, Map<string, Map<string, Array<PendingEntry>>>>()

function getPendingBatches(queryClient: QueryClient) {
  let batches = pendingBatchesByClient.get(queryClient)
  if (!batches) {
    batches = new Map()
    pendingBatchesByClient.set(queryClient, batches)
  }
  return batches
}

function buildParamsKey(params: BatchRequestParams): string {
  return JSON.stringify([params.region, params.checkIn, params.checkOut, params.occupancies, params.flexibleMonths, params.flexibleNights, params.viewerKey])
}

/**
 * Mirrors svc-trip's `MAX_BATCH_OFFER_IDS`; exceeding it is a 400, not a truncation.
 *
 * KNOWN LIMIT, worth stating because svc-trip's per-offer budget interacts with it.
 * `PER_OFFER_TIMEOUT_MS` bounds each offer server-side, but not what the customer waits
 * for: the endpoint answers with a single JSON body after `Promise.allSettled` over every
 * id in the request, and `flushOfferIdChunk` below settles every waiter of that request
 * from one `.then`. So a request carrying several ids cannot return until its slowest offer
 * settles, and those cards appear together, gated by the worst of them. Progressive reveal
 * therefore helps ACROSS requests — the streaming case, where ids arrive one at a time —
 * but not within one. A message re-rendered from history sends all its ids at once and is
 * gated as before.
 *
 * Splitting to one id per request would confine the budget completely. Deliberately not
 * done here: it reverses a tested batching contract and removes the bound on concurrent
 * requests (the commit-free reconnect backstop below can re-register every card in a
 * thread, which chunking currently caps at ceil(N/50) requests rather than N).
 */
const MAX_BATCH_OFFER_IDS_PER_REQUEST = 50

/**
 * Puts everything registered under `paramsKey` on the wire as a single request. A no-op when
 * nothing is pending, so it is safe to call after every render.
 */
function flushPendingBatch(queryClient: QueryClient, paramsKey: string, params: BatchRequestParams) {
  const batches = getPendingBatches(queryClient)
  const batch = batches.get(paramsKey)
  if (!batch) return
  // Detaching before the request is issued is what scopes a response to its own ids: an id
  // registered from here on accumulates into the next batch instead of joining a request that
  // has already left, so no response can ever settle a waiter it did not ask for.
  batches.delete(paramsKey)

  // Chunked, because the endpoint rejects more than MAX_BATCH_OFFER_IDS with a 400 and this
  // map has no natural bound. Per commit it holds one carousel's ids, but the reconnect
  // backstop above is commit-free: regaining the network re-registers every stale card in
  // every carousel in the thread into ONE pending batch under the same paramsKey, and a
  // single microtask flushes them together. Six carousels of ten offers on the same
  // dates/occupancy clears 50 and used to empty the entire thread at once.
  // Each chunk settles only its own waiters, so one failed chunk cannot fail the others.
  const pendingOfferIds = Array.from(batch.keys())
  for (let start = 0; start < pendingOfferIds.length; start += MAX_BATCH_OFFER_IDS_PER_REQUEST) {
    const chunkOfferIds = pendingOfferIds.slice(start, start + MAX_BATCH_OFFER_IDS_PER_REQUEST)
    flushOfferIdChunk(batch, chunkOfferIds, params)
  }
}

function flushOfferIdChunk(
  batch: Map<string, Array<PendingEntry>>,
  chunkOfferIds: Array<string>,
  params: BatchRequestParams,
) {
  getOfferDetailBatch({
    offerIds: chunkOfferIds,
    region: params.region,
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    flexibleMonths: params.flexibleMonths,
    flexibleNights: params.flexibleNights,
    occupancies: params.occupancies,
  })
    .then((batchResults) => {
      // Only ids in this detached `batch` can be settled, so an entry the endpoint volunteers
      // for an id this request never asked about has nowhere to be written and cannot clobber a
      // fresher answer a concurrent request already delivered. (Looking ids up in the response
      // rather than iterating it is just convenience — the scoping is the detached map.)
      const entriesByOfferId = new Map(batchResults.map((entry) => [entry.offerId, entry]))
      // Iterating THIS chunk's ids, not the whole batch: a sibling chunk's waiters must not
      // be resolved with `null` just because they were absent from this chunk's response.
      chunkOfferIds.forEach((offerId) => {
        // `null`, not `undefined`, which react-query rejects as a query result. An id the
        // endpoint omits stays unanswered rather than being reported as a failure — the
        // endpoint answers 1:1 today, so this is unreachable (see `useCarouselOfferSearch`).
        const entry = entriesByOfferId.get(offerId) ?? null
        batch.get(offerId)?.forEach((waiter) => waiter.resolve(entry))
      })
    })
    .catch((error: unknown) => {
      // A failed request fails every id IT carried — this chunk's, not the whole flush's —
      // which is what makes a total failure surface as `error` while a per-offer
      // `status: 'error'` stays a successful result.
      chunkOfferIds.forEach((offerId) => {
        batch.get(offerId)?.forEach((waiter) => waiter.reject(error))
      })
    })
}

function registerOfferId(
  queryClient: QueryClient,
  paramsKey: string,
  params: BatchRequestParams,
  offerId: string,
): Promise<BatchOfferDetailResult | null> {
  const batches = getPendingBatches(queryClient)
  let batch = batches.get(paramsKey)
  if (!batch) {
    batch = new Map()
    batches.set(paramsKey, batch)
    // Backstop for a fetch no commit drove, which has no accompanying effect to flush it. The
    // app disables `refetchOnWindowFocus` globally, but leaves `refetchOnReconnect` on, so
    // regaining the network after these entries go stale re-prices every visible card.
    queueMicrotask(() => flushPendingBatch(queryClient, paramsKey, params))
  }
  return new Promise((resolve, reject) => {
    const waiters = batch.get(offerId)
    if (waiters) waiters.push({ resolve, reject })
    else batch.set(offerId, [{ resolve, reject }])
  })
}

/**
 * Fetches offer details for `offerIds` in as few requests as possible.
 *
 * Each offer id is its own react-query entry, so an id is fetched once and never re-asked for
 * while the assistant streams more of them in — re-requesting the whole accumulated list on
 * every patch would resolve 1+2+…+N offer details for an N-offer carousel. The batch endpoint
 * is reconciled with the per-id cache by `flushPendingBatch`, which collects the ids one render
 * newly asked for and sends them as one request.
 *
 * `results` holds one entry per offer id the current list still asks for and that has been
 * answered, in list order — callers that care about a different order must map it back through
 * their own id list.
 */
export default function useOfferDetailBatch(offerIds: Array<string>, filters: OfferDetailBatchFilters) {
  const { currentRegionCode } = useContext(GeoContext)
  const queryClient = useQueryClient()
  const viewerKey = useOfferDetailViewerKey()
  const stableOfferIds = useStableObjectReference(offerIds)
  const stableFilters = useStableObjectReference(filters)

  const requestParams = useMemo<BatchRequestParams>(() => ({
    viewerKey,
    region: currentRegionCode,
    checkIn: stableFilters.checkIn,
    checkOut: stableFilters.checkOut,
    flexibleMonths: stableFilters.flexibleMonths,
    flexibleNights: stableFilters.flexibleNights,
    // The occupancy picker uses -1 until a child's age is chosen; never send placeholders.
    occupancies: stableFilters.occupancy ?
        JSON.stringify(stableFilters.occupancy.map((room) => ({
          adults: room.adults,
          children: room.children,
          childrenAge: sanitiseChildrenAges(room.childrenAge ?? []),
        }))) :
      undefined,
  }), [currentRegionCode, stableFilters, viewerKey])
  const paramsKey = useMemo(() => buildParamsKey(requestParams), [requestParams])

  // A duplicated id in the streamed list is one query rather than two.
  const uniqueOfferIds = useMemo(() => Array.from(new Set(stableOfferIds)), [stableOfferIds])

  // The set and order of ids this hook observes, plus what they are priced under, as content
  // rather than identity — so the gate below cannot be opened by a memo cache being discarded.
  const observedKey = useMemo(
    () => JSON.stringify([paramsKey, uniqueOfferIds]),
    [paramsKey, uniqueOfferIds],
  )
  const flushedKeyRef = useRef<string | undefined>(undefined)

  const queries = useMemo(() => uniqueOfferIds.map((offerId) => ({
    queryKey: OfferDetailKeys.batchEntry(
      offerId,
      requestParams.region,
      requestParams.checkIn,
      requestParams.checkOut,
      requestParams.occupancies,
      requestParams.flexibleMonths,
      requestParams.flexibleNights,
      requestParams.viewerKey,
    ),
    queryFn: () => registerOfferId(queryClient, paramsKey, requestParams, offerId),
    staleTime: OFFER_DETAIL_STALE_TIME,
    // Restates the app-wide default (`src/client/index.tsx` sets `retry: false` too) rather
    // than changing anything — kept explicit because the re-ask semantics below are built on
    // it and a future change to the global default must not silently change them. The
    // suppression tests ("does not retry by itself while the offer list is unchanged", "asks
    // exactly once more against a permanently dead endpoint") are what actually pin it.
    //
    // Re-asking is driven by the list changing (below), not by react-query.
    //
    // Known consequence, not an oversight: a transient whole-request failure is recoverable
    // only by remount, reconnect or an id-list change — and batching means it takes every card
    // in the carousel at once. svc-trip also marks individual offers retryable
    // (`reason: 'timeout' | 'upstream_error'`), which nothing here acts on; those arrive inside
    // a 200 as per-offer entries, so honouring them means making the query function throw and
    // giving up the reason-bearing card. Both are a UX decision rather than a retry-policy
    // tweak — raised in review #37389 [9/50], deliberately left for a follow-up.
    retry: false,
  })), [paramsKey, queryClient, requestParams, uniqueOfferIds])

  const queryResults = useQueries({ queries })

  // `useQueries` sets its observers up in an effect declared above this one, so every query
  // function this commit triggered has already registered its id by the time this runs — the
  // request that goes out here is exactly the ids this render newly asked for, and nothing that
  // an earlier render already has cached or in flight.
  useEffect(() => {
    // Two things make a flush due. A query function registered an id — an append, or a first
    // render — or the list changed shape without any id being new, which is a shrink or a
    // reorder: every id is already cached, possibly in its error state, so nothing re-registers
    // by itself and the pending map stays empty. The old hook re-asked on ANY change to the
    // list, and both halves of this are needed to match it.
    // Removing this gate entirely exhausts the V8 heap (measured), so keep both halves of it.
    const listChanged = flushedKeyRef.current !== observedKey
    if (!listChanged && !getPendingBatches(queryClient).has(paramsKey)) return

    // Recording the key unconditionally, on every pass through the gate, is what bounds the
    // resets below: the next render finds the same key with nothing left pending and returns
    // above, so one change to the list buys exactly one attempt no matter how many renders the
    // failure causes. Both ways of weakening that were measured. Deleting this line is an
    // unbounded reset → refetch → reject → reset cycle that exhausts the heap; making it
    // conditional on something being pending is the same runaway, caught by 'asks exactly once
    // more when a list change is retried against a permanently dead endpoint' as an ordinary red.
    flushedKeyRef.current = observedKey

    // Something new is going on the wire, so the ids a previous request failed ride along with
    // it and their failure is superseded — matching the old hook's "a new fetch starts from a
    // clean slate".
    queryResults.forEach((query, index) => {
      // `resetQueries` swallows the refetch's own failure (`throwOnError` defaults to false), so
      // the returned promise settles either way and needs no rejection handling here.
      if (query.isError) queryClient.resetQueries({ queryKey: queries[index].queryKey, exact: true })
    })

    flushPendingBatch(queryClient, paramsKey, requestParams)
  })

  const answeredEntries = queryResults.flatMap((query) => query.data ? [query.data] : [])
  // `useQueries` hands back a new array every render while the entries inside it are stable
  // cache references, so without this the carousel would see new results on every render and
  // re-map every offer it is showing.
  const resultsRef = useRef<Array<BatchOfferDetailResult>>([])
  const resultsChanged = answeredEntries.length !== resultsRef.current.length ||
    answeredEntries.some((entry, index) => entry !== resultsRef.current[index])
  if (resultsChanged) resultsRef.current = answeredEntries

  // Asking only the queries for ids the list currently holds is what makes this "is anything
  // the list still wants outstanding" rather than "is anything in flight": a request the list
  // has moved on from has no observer here, so it cannot hold the carousel loading.
  const isLoading = queryResults.some((query) => query.isLoading && query.fetchStatus !== 'idle')

  const failure = queryResults.find((query) => query.isError)?.error
  const error = useMemo(() => {
    if (failure === undefined) return undefined
    return failure instanceof Error ? failure : new Error('Failed to fetch offer details')
  }, [failure])

  return { results: resultsRef.current, isLoading, error }
}
